import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";

const FeatureCard = styled(Box)`
  border-radius: 16px;
  padding: 32px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  }
`;

const CardContent = styled(Box)`
  position: relative;
  z-index: 2;
  background: ${props => props.theme.palette.background.paper};
  border-radius: 12px;
  padding: 24px;
  margin-top: auto;
  text-align: center;
`;

const CardTitle = styled(Typography)`
  color: ${props => props.theme.palette.text.primary} !important;
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 24px;
  line-height: 1.2;
  padding-bottom: 16px;
`;

const CardSubtitle = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary} !important;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 400;
`;

export const LocationCard = () => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [bgPosition, setBgPosition] = React.useState({ x: 50, y: 50 });
  const controls = useAnimation();

  // Autoplay animation loop
  useEffect(() => {
    const animate = async () => {
      // Create a slow circular motion
      const radius = 20; // How far from center the background moves
      const duration = 8; // 8 seconds for one complete cycle
      
      await controls.start({
        backgroundPosition: [
          `${50 + radius}% ${50}%`,
          `${50}% ${50 + radius}%`,
          `${50 - radius}% ${50}%`,
          `${50}% ${50 - radius}%`,
          `${50 + radius}% ${50}%`
        ],
        transition: {
          duration: duration,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop"
        }
      });
    };
    
    animate();
  }, [controls]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Calculate normalized position (0 to 1)
      const normalizedX = x / rect.width;
      const normalizedY = y / rect.height;
      
      // Convert to percentage for background position
      const bgX = normalizedX * 100;
      const bgY = normalizedY * 100;
      
      // Update state values for animation
      setBgPosition({ x: bgX, y: bgY });
    }
  };

  return (
    <FeatureCard 
      ref={cardRef}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        data-motion
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url(/white-sample.png)',
          backgroundSize: '120%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          opacity: 0.8,
          zIndex: 1,
          borderRadius: '16px',
        }}
        animate={{
          backgroundPosition: `${bgPosition.x}% ${bgPosition.y}%`,
        }}
        transition={{
          backgroundPosition: {
            type: "tween",
            ease: "easeOut",
            duration: 0.3
          }
        }}
      />
      
      {/* Pin elements that move with the background */}
      <motion.img
        src="/pin-icon.png"
        alt="Pin 1"
        style={{
          position: 'absolute',
          top: '25%',
          left: '30%',
          width: '22px',
          height: '22px',
          zIndex: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
        animate={{
          x: [0, 10, 0, -10, 0],
          y: [0, -10, 0, 10, 0],
        }}
        transition={{
          x: { duration: 6, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
          y: { duration: 6, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
        }}
      />
      <motion.img
        src="/pin-icon.png"
        alt="Pin 1"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '22px',
          height: '22px',
          zIndex: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
        animate={{
          x: [0, 10, 0, -10, 0],
          y: [0, -10, 0, 10, 0],
        }}
        transition={{
          x: { duration: 15, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
          y: { duration: 15, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
        }}
      />
      
      <motion.img
        src="/pin-icon.png"
        alt="Pin 2"
        style={{
          position: 'absolute',
          top: '60%',
          left: '70%',
          width: '22px',
          height: '22px',
          zIndex: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
        animate={{
          x: [0, -8, 0, 8, 0],
          y: [0, 8, 0, -8, 0],
        }}
        transition={{
          x: { duration: 7, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
          y: { duration: 7, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
        }}
      />
      
      <motion.img
        src="/pin-icon.png"
        alt="Pin 3"
        style={{
          position: 'absolute',
          top: '40%',
          left: '20%',
          width: '22px',
          height: '22px',
          zIndex: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
        animate={{
          x: [0, 12, 0, -12, 0],
          y: [0, -12, 0, 12, 0],
        }}
        transition={{
          x: { duration: 8, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
          y: { duration: 8, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
        }}
      />
      
      <motion.img
        src="/pin-icon.png"
        alt="Pin 4"
        style={{
          position: 'absolute',
          top: '75%',
          left: '45%',
          width: '22px',
          height: '22px',
          zIndex: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
        animate={{
          x: [0, -6, 0, 6, 0],
          y: [0, 6, 0, -6, 0],
        }}
        transition={{
          x: { duration: 9, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
          y: { duration: 9, ease: "easeInOut", repeat: Infinity, repeatType: "loop" },
        }}
      />
      
      <CardContent>
        <CardTitle variant="h4">
          Pin Anywhere
        </CardTitle>
        <CardSubtitle variant="body1">
          Pick any location and add over 20 pins of your favourite places
        </CardSubtitle>
      </CardContent>
    </FeatureCard>
  )
};
